"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  Calendar,
  Check,
  CheckCircle2,
  Clock3,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Sparkles,
  User,
  Video,
  X,
  ChevronDown,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { isValidPhoneNumber } from "libphonenumber-js";
import useToastNotification from "@/hook/useToastNotification";
import axiosInstance from "@/utils/axiosInstance";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  bookTheSlot,
  getAvailableSlot,
} from "@/store/feature/talkToExpert/talkToExpert";
import { getDateSlots } from "@/utils/getDateSlots";
import { sendCustomGaEvent } from "@/scripts/ga";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hook/useAuth";
import { sentOtp, verifyOtp } from "@/store/feature/user/userApi";
import { createCustomTokenWithUserId } from "@/app/actions/loginUserWithOTP";
import { registerUserWithOTP } from "@/app/actions/registerUserWithOTP";
import { getStoredTrackingParams } from "@/utils/utmTracker";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { app, db } from "@/firebase/firebase";
import { doc, getDoc } from "firebase/firestore";
import { getUserMeeting } from "@/store/feature/user/userApi";
import { getAuth, signInWithCustomToken } from "firebase/auth";

interface UserDetails {
  user_id?: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  email?: string;
  phoneNumber?: string;
  primary_phone_number?: string;
  [key: string]: any;
}

interface FloatingExpertCallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Split Steps:
// 1. "session-type": Pick Call Type (15-min phone / 30-min meet / WhatsApp)
// 2. "slot-picker": Big date buttons filling full width + Available time slots
// 3. "contact-form": Name, Email, Phone (locked to India)
// 4. "otp-verification": OTP verification & confirm booking
// 5. "success": Confirmation screen
type ExpertStep = "session-type" | "slot-picker" | "contact-form" | "otp-verification" | "success";

const SLOT_TIME_ZONE = "Asia/Kolkata";
const allSlots = getDateSlots();
const nonSundaySlots = allSlots.filter(({ fullDate }) => {
  const date = new Date(`${fullDate}T00:00:00`);
  return !Number.isNaN(date.getTime()) && date.getDay() !== 0;
});

interface SlotItem {
  time: string;
  startUtc: string;
  isUnavailable?: boolean;
}

const stepVariants = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
};

const extractErrorMessage = (error: unknown): string | null => {
  if (!error) return null;
  const err = error as any;
  if (err?.response?.data?.message) {
    const msg = String(err.response.data.message);
    if (!msg.toLowerCase().includes("request failed with status code")) return msg;
  }
  if (err?.data?.message) {
    const msg = String(err.data.message);
    if (!msg.toLowerCase().includes("request failed with status code")) return msg;
  }
  if (err?.message && typeof err.message === "string") {
    if (!err.message.toLowerCase().includes("request failed with status code")) return err.message;
  }
  return null;
};

export const FloatingExpertCallModal: React.FC<FloatingExpertCallModalProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { user } = useAuth();
  const { availableSlots, counsellorId } = useAppSelector(
    (state) => state.talkToExpert,
  );
  const { meetings = [] } = useAppSelector((state) => state.userProfile);
  const { showToast } = useToastNotification();

  // Step state
  const [expertStep, setExpertStep] = useState<ExpertStep>("session-type");

  // Selection & Details Form Fields
  const [sessionType, setSessionType] = useState<"telephonic" | "video">("telephonic");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);

  // Validation Errors
  const [fullNameError, setFullNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  // OTP State
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [userAlreadyExists, setUserAlreadyExists] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Slot Picker State
  const [selectedDate, setSelectedDate] = useState(
    nonSundaySlots[0]?.fullDate || allSlots[0]?.fullDate || "",
  );
  const [selectedSlotTime, setSelectedSlotTime] = useState("");
  const [availableSlotList, setAvailableSlotList] = useState<SlotItem[]>([]);
  const [isFetchingSlots, setIsFetchingSlots] = useState(false);
  const [isSubmittingBooking, setIsSubmittingBooking] = useState(false);

  // Fetch user profile from Firestore when authenticated
  useEffect(() => {
    const fetchFirestoreUser = async () => {
      if (!user?.uid || user.isAnonymous) {
        setUserDetails(null);
        return;
      }
      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setUserDetails(userSnap.data() as UserDetails);
        }
      } catch (err) {
        console.error("Error fetching user profile from Firestore:", err);
      }
    };
    fetchFirestoreUser();
  }, [user]);

  // Pre-fill if logged in user exists or firestore details are loaded, clear if logged out
  useEffect(() => {
    if (user && !user.isAnonymous) {
      const parts = [userDetails?.firstName, userDetails?.middleName, userDetails?.lastName]
        .map((p) => (p ? String(p).trim() : ""))
        .filter((p) => p !== "" && p.toLowerCase() !== "null");
      const derivedName = parts.join(" ").trim();
      
      const rawDisplayName = user.displayName ? String(user.displayName).replace(/null/gi, "").trim() : "";
      const resolvedName = derivedName || rawDisplayName;
      
      const rawEmail = userDetails?.email || user.email || "";
      const resolvedEmail = rawEmail.toLowerCase().includes("null") ? "" : rawEmail;
      
      const rawPhone =
        userDetails?.phoneNumber ||
        userDetails?.primary_phone_number ||
        user.phoneNumber ||
        "";
      const resolvedPhone = rawPhone.toLowerCase().includes("null") ? "" : rawPhone;

      setFullName(resolvedName);
      setEmail(resolvedEmail);
      setPhone(resolvedPhone);
    } else {
      // User is logged out or anonymous -> Reset state to avoid showing previous user's details
      setUserDetails(null);
      setFullName("");
      setEmail("");
      setPhone("");
    }
  }, [user, userDetails]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("!overflow-hidden");
    } else {
      document.body.classList.remove("!overflow-hidden");
    }
    return () => document.body.classList.remove("!overflow-hidden");
  }, [isOpen]);

  const handleClose = useCallback(() => {
    onClose();
    setExpertStep("session-type");
    setIsOtpSent(false);
    setSelectedSlotTime("");
    setOtp("");
    setOtpError("");
    setFullNameError("");
    setEmailError("");
    setPhoneError("");
    if (timerRef.current) clearInterval(timerRef.current);
  }, [onClose]);

  const startCountdown = (duration = 60) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setResendCountdown(duration);
    timerRef.current = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const fetchSlotsForDate = useCallback(
    async (date: string, type: "telephonic" | "video") => {
      setIsFetchingSlots(true);
      try {
        await dispatch(getAvailableSlot({ date, sessionType: type }));
      } catch (error) {
        console.error("Failed to load slots:", error);
      } finally {
        setIsFetchingSlots(false);
      }
    },
    [dispatch],
  );

  // Fetch slots whenever slot-picker step is active or date/sessionType changes
  useEffect(() => {
    if (isOpen && selectedDate) {
      fetchSlotsForDate(selectedDate, sessionType);
    }
  }, [isOpen, selectedDate, sessionType, fetchSlotsForDate]);

  // Process available slots
  useEffect(() => {
    if (!availableSlots || availableSlots.length === 0) {
      setAvailableSlotList([]);
      return;
    }

    const durationMinutes = sessionType === "video" ? 30 : 15;
    const now = new Date();
    const currentTime = now.getTime();

    const todayInKolkata = new Intl.DateTimeFormat("en-CA", {
      timeZone: SLOT_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);

    const timeFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: SLOT_TIME_ZONE,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    const dateFormatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: SLOT_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    const bookedRanges = meetings
      .filter((meeting) => {
        const status = meeting?.meetingStatus?.toLowerCase();
        return ![
          "client_canceled",
          "counsellor_canceled",
          "canceled",
          "cancelled",
          "missed",
        ].includes(status);
      })
      .map((meeting) => ({
        start: new Date(meeting.meetingMetadata.start.dateTime).getTime(),
        end: new Date(meeting.meetingMetadata.end.dateTime).getTime(),
      }));

    const slotsForDate: SlotItem[] = availableSlots
      .filter(({ start }) => {
        const slotDateStr = dateFormatter.format(new Date(start));
        return slotDateStr === selectedDate;
      })
      .map(({ start }) => ({
        time: timeFormatter.format(new Date(start)),
        startUtc: start,
      }));

    const processed: SlotItem[] = availableSlots
      .filter(({ start, end }) => {
        const slotMs = new Date(start).getTime();
        const slotEndMs = end
          ? new Date(end).getTime()
          : slotMs + durationMinutes * 60 * 1000;
        const slotDateStr = dateFormatter.format(new Date(start));

        if (slotDateStr !== selectedDate) return false;
        if (selectedDate === todayInKolkata && slotMs <= currentTime) return false;

        const clashes = bookedRanges.some(
          (m) => slotMs < m.end && slotEndMs > m.start,
        );
        return !clashes;
      })
      .map(({ start }) => ({
        time: timeFormatter.format(new Date(start)),
        startUtc: start,
      }));

    const selectedStillInDate = slotsForDate.find(
      (slot) => slot.time === selectedSlotTime,
    );
    const shouldKeepSelected =
      Boolean(selectedSlotTime) &&
      Boolean(selectedStillInDate) &&
      !processed.some((slot) => slot.time === selectedSlotTime);

    const slotListToDisplay = shouldKeepSelected
      ? [
        ...processed,
        {
          ...selectedStillInDate!,
          isUnavailable: true,
        },
      ].sort(
        (a, b) =>
          new Date(a.startUtc).getTime() - new Date(b.startUtc).getTime(),
      )
      : processed;

    setAvailableSlotList(slotListToDisplay);

    setSelectedSlotTime((current) => {
      if (processed.some((s) => s.time === current)) return current;
      if (slotsForDate.some((s) => s.time === current)) return current;
      return "";
    });
  }, [availableSlots, meetings, selectedDate, selectedSlotTime, sessionType]);

  const handlePhoneChange = useCallback((value: string) => {
    setPhone(value);
    if (value && isValidPhoneNumber(value)) {
      setPhoneError("");
    }
  }, []);

  const validateDetails = () => {
    let isValid = true;

    if (!fullName.trim()) {
      setFullNameError("Please enter your full name");
      isValid = false;
    } else {
      setFullNameError("");
    }

    if (!email.trim()) {
      setEmailError("Please enter your email");
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailError("Please enter a valid email address");
      isValid = false;
    } else {
      setEmailError("");
    }

    if (!phone || !isValidPhoneNumber(phone)) {
      setPhoneError("Please enter a valid 10-digit mobile number");
      isValid = false;
    } else {
      setPhoneError("");
    }

    return isValid;
  };

  // Step 1 -> Pick Call Type and automatically advance to Step 2 (Slot Picker)
  const handleSelectCallType = (type: "telephonic" | "video") => {
    setSessionType(type);
    setSelectedSlotTime("");
    setExpertStep("slot-picker");
  };

  // Step 2 -> Pick Slot and advance to Step 3 (or book directly if user is logged in with valid details)
  const handleProceedToDetails = async () => {
    if (!selectedSlotTime) {
      showToast("Please select an available time slot to continue.", "error");
      return;
    }

    // If user is already logged in with valid name and phone, skip contact form & OTP and book immediately
    const derivedName = [userDetails?.firstName, userDetails?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const resolvedName = (fullName.trim() || derivedName || user?.displayName || "")
      .replace(/null/gi, "")
      .trim();
    const resolvedPhone =
      phone.trim() ||
      userDetails?.phoneNumber ||
      userDetails?.primary_phone_number ||
      user?.phoneNumber ||
      "";

    if (user && !user.isAnonymous && resolvedName && resolvedPhone && isValidPhoneNumber(resolvedPhone)) {
      await executeBooking();
      return;
    }

    setExpertStep("contact-form");
  };

  // Step 3 -> Send OTP and advance to Step 4 (OTP Verification)
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateDetails()) {
      showToast("Please fill in your name, email, and phone number correctly.", "error");
      return;
    }

    // If user is already authenticated with this mobile, execute booking directly
    if (user && !user.isAnonymous && (user.phoneNumber === phone || userDetails?.phoneNumber === phone)) {
      await executeBooking();
      return;
    }

    setIsSendingOtp(true);
    setOtpError("");

    try {
      // Send OTP
      const response = await dispatch(sentOtp({ phoneNumber: phone })).unwrap();
      if (response?.user_id) {
        localStorage.setItem("userId", response.user_id);
        setUserAlreadyExists(true);
      } else {
        localStorage.removeItem("userId");
        setUserAlreadyExists(false);
      }

      showToast("OTP sent successfully to your mobile number!", "success");
      startCountdown(60);
      setIsOtpSent(true);
    } catch (error: any) {
      console.error("Error sending OTP:", error);
      showToast(error?.message || "Failed to send OTP. Please try again.", "error");
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (resendCountdown > 0 || isSendingOtp) return;
    setIsSendingOtp(true);
    setOtpError("");
    try {
      const response = await dispatch(sentOtp({ phoneNumber: phone })).unwrap();
      if (response?.user_id) {
        localStorage.setItem("userId", response.user_id);
        setUserAlreadyExists(true);
      }
      showToast("New OTP sent successfully!", "success");
      setOtp("");
      startCountdown(60);
    } catch (error: any) {
      setOtpError(error?.message || "Failed to resend OTP");
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Core booking dispatcher
  const executeBooking = async () => {
    let slotStartTime: Date;
    let slotEndTime: Date;

    const chosenSlot = availableSlotList.find((s) => s.time === selectedSlotTime);
    const durationMinutes = sessionType === "telephonic" ? 15 : 30;

    if (chosenSlot?.startUtc) {
      slotStartTime = new Date(chosenSlot.startUtc);
      slotEndTime = new Date(slotStartTime.getTime() + durationMinutes * 60 * 1000);
    } else {
      // Fallback date/time if no slots returned from backend
      const baseDate = selectedDate || new Date().toISOString().split("T")[0];
      slotStartTime = new Date(`${baseDate}T10:30:00.000Z`);
      slotEndTime = new Date(slotStartTime.getTime() + durationMinutes * 60 * 1000);
    }

    setIsSubmittingBooking(true);

    try {
      const storedUserId = typeof window !== "undefined" ? localStorage.getItem("userId") : null;
      
      const rawDerivedName = [userDetails?.firstName, userDetails?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      const cleanName = (fullName.trim() || rawDerivedName || user?.displayName || "Student")
        .replace(/null/gi, "")
        .trim() || "Student";

      const cleanEmail =
        email.trim() ||
        userDetails?.email ||
        user?.email ||
        "student@edvia.com";

      const cleanPhone =
        phone.trim() ||
        userDetails?.phoneNumber ||
        userDetails?.primary_phone_number ||
        user?.phoneNumber ||
        "+919999999999";

      const studentIdToPass = userDetails?.user_id || userDetails?.userId || user?.uid || storedUserId || undefined;

      // Ensure local state is updated with clean values so Success Screen never shows "null null"
      if (!fullName || fullName.includes("null")) {
        setFullName(cleanName);
      }
      if (!email) {
        setEmail(cleanEmail);
      }
      if (!phone) {
        setPhone(cleanPhone);
      }

      const bookingPayload = {
        startTime: slotStartTime,
        endTime: slotEndTime,
        customerInfo: {
          name: cleanName,
          email: cleanEmail,
          phone: cleanPhone,
          sessionType,
          studentId: studentIdToPass,
          counsellorId: counsellorId || undefined,
        },
      };

      const res = await dispatch(bookTheSlot(bookingPayload));
      const bookingResult = res.payload as { status?: boolean | number } | undefined;

      if (res.meta.requestStatus === "fulfilled" || bookingResult?.status === true) {
        showToast("Slot booked successfully!", "success");
        void sendCustomGaEvent("floating_expert_booking_success", {
          session_type: sessionType,
          date: selectedDate,
          time: selectedSlotTime,
        });
        if (user?.uid) {
          dispatch(getUserMeeting(user.uid));
        }
        setExpertStep("success");
      } else {
        const friendlyMsg =
          extractErrorMessage(res.payload) ||
          "Failed to book slot. Please try another slot.";
        showToast(friendlyMsg, "error");
      }
    } catch (error: any) {
      console.error("Booking error:", error);
      const friendlyMsg =
        extractErrorMessage(error) ||
        error?.message ||
        "Failed to book slot. Please try another time.";
      showToast(friendlyMsg, "error");
    } finally {
      setIsSubmittingBooking(false);
    }
  };

  // Step 4 -> Verify OTP and book session immediately
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length < 6) {
      setOtpError("Please enter the complete 6-digit OTP");
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError("");

    try {
      // Step A: Verify OTP with backend
      await dispatch(
        verifyOtp({ phoneNumber: phone, otp }),
      ).unwrap();

      localStorage.setItem(
        "otpVerified",
        JSON.stringify({ message: "success", time: new Date() }),
      );

      // Step B: Attempt background registration or login for non-logged in users
      try {
        const storedUserId = localStorage.getItem("userId");

        if (userAlreadyExists || storedUserId) {
          // CASE 1: User already registered -> Log in via custom token
          const tokenRes = await createCustomTokenWithUserId({
            uid: storedUserId || "",
          });
          if (tokenRes.success && tokenRes.customToken) {
            const firebaseAuth = getAuth(app);
            await signInWithCustomToken(firebaseAuth, tokenRes.customToken);
            localStorage.setItem("userDetails", JSON.stringify(storedUserId));
          }
        } else {
          // CASE 2: Non-registered user -> Register via registerUserWithOTP
          const nameParts = fullName.trim().split(/\s+/);
          const firstName = nameParts[0] || "Student";
          const lastName = nameParts.length >= 2 ? nameParts[nameParts.length - 1] : null;
          const middleName = nameParts.length > 2 ? nameParts[1] : null;
          const trackingParams = getStoredTrackingParams();

          const regRes = await registerUserWithOTP({
            firstName,
            middleName,
            lastName,
            email: email || null,
            phone,
            ...trackingParams,
            source: trackingParams.utm_campaign || trackingParams.source || "floating_expert_call",
          });

          if (regRes.success && regRes.customToken) {
            const firebaseAuth = getAuth(app);
            await signInWithCustomToken(firebaseAuth, regRes.customToken);
            if (regRes.userDetails?.userId) {
              localStorage.setItem("userDetails", JSON.stringify(regRes.userDetails.userId));
            }
          }
        }
      } catch (authErr) {
        console.warn("Background user registration warning:", authErr);
        // Do not throw here: OTP is already verified, so booking proceed below!
      }

      showToast("Mobile verified! Confirming your booking...", "success");
      await executeBooking();
    } catch (error: any) {
      console.error("OTP verification error:", error);
      const friendlyMsg = extractErrorMessage(error) || "Invalid or expired OTP. Please check and try again.";
      setOtpError(friendlyMsg);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  // Formatted date string for success card e.g. "Today 7 PM" or "Sep 16, 11:30 AM"
  const formattedSlotReachout = useMemo(() => {
    if (!selectedSlotTime) return "soon";
    const today = new Intl.DateTimeFormat("en-CA", {
      timeZone: SLOT_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());

    if (selectedDate === today) {
      return `Today ${selectedSlotTime}`;
    }

    const parsed = new Date(`${selectedDate}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return `${selectedDate} at ${selectedSlotTime}`;
    const dayMonth = parsed.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    return `${dayMonth} at ${selectedSlotTime}`;
  }, [selectedDate, selectedSlotTime]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <span
        onClick={handleClose}
        className="fixed bottom-0 left-0 w-full h-full z-[70] bg-black/50 backdrop-blur-[2px] animate-fade-in"
      />

      {/* Bottom Sheet Modal with balanced max-width */}
      <motion.div
        key="floating-expert-drawer"
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        className="max-h-[92vh] overflow-y-auto fixed left-1/2 -translate-x-1/2 bg-[#F7F5FF] shadow-[0px_-4px_20px_0px_#0000001F] px-3.5 sm:px-10 md:px-12 lg:px-16 xl:px-20 3xl:px-[90px] pt-5 sm:pt-8 pb-6 sm:pb-12 w-full rounded-t-[28px] sm:rounded-t-[32px] bottom-0 z-[75] ff_poppins"
      >
        {/* Header with Title and Close Button */}
        <div className="flex items-center justify-between pb-3 border-b border-slate-200">
          <div>
            <p className="font-bold text-base sm:text-lg md:text-xl lg:text-2xl text-slate-900">
              {expertStep === "success"
                ? "Session Reserved"
                : isOtpSent
                  ? "Enter 6-digit Verification Code"
                  : expertStep === "contact-form"
                    ? "Enter Contact Details"
                    : expertStep === "slot-picker"
                      ? "Select Date & Time Slot"
                      : "1-on-1 Expert Guidance"}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="size-[32px] md:size-[38px] xl:size-[44px] bg-slate-200/80 hover:bg-slate-300 transition flex items-center justify-center rounded-full cursor-pointer"
            aria-label="Close"
          >
            <X className="size-4 md:size-6 text-slate-700" />
          </button>
        </div>

        <AnimatePresence mode="wait">
          {/* ────────── STEP 1: PICK CALL TYPE (SPLIT VIEW) ────────── */}
          {expertStep === "session-type" && (
            <motion.div
              key="session-type-step"
              variants={stepVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="pt-3 sm:pt-4"
            >
              <p className="font-semibold text-xs sm:text-base text-slate-800 mb-3 tracking-wide uppercase">
                HOW WOULD YOU LIKE TO GET GUIDANCE?
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 sm:gap-4">
                {/* 15-min Phone Call */}
                <button
                  type="button"
                  onClick={() => handleSelectCallType("telephonic")}
                  className={`bg-white border rounded-[18px] sm:rounded-[20px] p-3.5 sm:p-6 flex items-center gap-3.5 transition-all cursor-pointer text-left hover:shadow-md group ${sessionType === "telephonic"
                      ? "border-[#1776F0] ring-2 ring-[#1776F0]/20 shadow-sm"
                      : "border-slate-200 hover:border-[#1776F0]"
                    }`}
                >
                  <div className="w-10 h-10 sm:w-13 sm:h-13 rounded-xl sm:rounded-2xl bg-[#EFF6FF] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-[#1776F0]" />
                  </div>
                  <div>
                    <p className="ff_poppins text-sm sm:text-lg font-bold text-slate-900 leading-tight">
                      15-min phone call
                    </p>
                    <p className="text-[11px] sm:text-sm ff_poppins font-medium text-slate-500 mt-0.5">
                      Quick clarity on courses & visa
                    </p>
                  </div>
                </button>

                {/* 30-min Google Meet */}
                <button
                  type="button"
                  onClick={() => handleSelectCallType("video")}
                  className={`bg-white border rounded-[18px] sm:rounded-[20px] p-3.5 sm:p-6 flex items-center gap-3.5 transition-all cursor-pointer text-left hover:shadow-md group ${sessionType === "video"
                      ? "border-[#8F3EBC] ring-2 ring-[#8F3EBC]/20 shadow-sm"
                      : "border-slate-200 hover:border-[#8F3EBC]"
                    }`}
                >
                  <div className="w-10 h-10 sm:w-13 sm:h-13 rounded-xl sm:rounded-2xl bg-[#FAF5FF] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <Video className="w-5 h-5 sm:w-6 sm:h-6 text-[#8F3EBC]" />
                  </div>
                  <div>
                    <p className="ff_poppins text-sm sm:text-lg font-bold text-slate-900 leading-tight">
                      30-min Google Meet
                    </p>
                    <p className="text-xs sm:text-sm ff_poppins font-medium text-slate-500 mt-1">
                      Deep profile & university review
                    </p>
                  </div>
                </button>

                {/* WhatsApp Chat */}
                <Link
                  href="https://wa.me/917347012405?text=Hi%20Edvia%2C%20I%20want%20guidance%20for%20studying%20abroad"
                  target="_blank"
                  className="bg-white border border-slate-200 hover:border-emerald-500 hover:shadow-md rounded-[20px] p-5 sm:p-6 flex items-center gap-4 transition-all group"
                >
                  <div className="w-13 h-13 rounded-2xl bg-[#E8F8EE] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <Image
                      src="/assets/images/png/aiCourseFinder/chat.png"
                      alt="WhatsApp"
                      width={32}
                      height={32}
                    />
                  </div>
                  <div>
                    <p className="ff_poppins text-base sm:text-lg font-bold text-slate-900 leading-tight">
                      Quick WhatsApp chat
                    </p>
                    <p className="text-xs sm:text-sm ff_poppins font-medium text-slate-500 mt-1">
                      Instant response with AI bot
                    </p>
                  </div>
                </Link>
              </div>
            </motion.div>
          )}

          {/* ────────── STEP 2: SELECT DATE & TIME SLOT (EXPANDED BIG BUTTONS) ────────── */}
          {expertStep === "slot-picker" && (
            <motion.div
              key="slot-picker-step"
              variants={stepVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="pt-4"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3.5">
                <button
                  type="button"
                  onClick={() => setExpertStep("session-type")}
                  className="text-xs sm:text-sm font-semibold text-slate-600 hover:text-black flex items-center gap-1 transition shrink-0 cursor-pointer"
                >
                  ← Change Mode
                </button>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-[11px] sm:text-xs font-semibold text-[#1776F0] shrink-0">
                  {sessionType === "telephonic" ? "📞 15-min Call" : "📹 30-min Meet"}
                </span>
              </div>

              <div className="bg-white border border-slate-200 rounded-[20px] sm:rounded-[24px] p-3.5 sm:p-7 shadow-sm">
                <p className="text-sm sm:text-lg font-bold text-slate-900 mb-0.5">
                  Select Date
                </p>
                <p className="text-[11px] sm:text-xs text-slate-500 mb-3.5">
                  Pick a preferred day for your session
                </p>

                {/* Balanced Date Buttons */}
                <div className="grid grid-cols-3 gap-2.5 sm:gap-3 mb-5">
                  {nonSundaySlots.map((slot) => {
                    const isSelected = selectedDate === slot.fullDate;
                    const todayStr = new Date().toISOString().split("T")[0];
                    const tomorrowDate = new Date();
                    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
                    const tomorrowStr = tomorrowDate.toISOString().split("T")[0];

                    const isToday = slot.fullDate === todayStr;
                    const isTomorrow = slot.fullDate === tomorrowStr;

                    const weekday = isToday
                      ? "Today"
                      : isTomorrow
                        ? "Tomorrow"
                        : new Date(`${slot.fullDate}T00:00:00`).toLocaleDateString("en-US", { weekday: "short" });

                    return (
                      <button
                        key={slot.fullDate}
                        type="button"
                        onClick={() => {
                          setSelectedDate(slot.fullDate);
                          setSelectedSlotTime("");
                        }}
                        className={`flex flex-col items-center justify-center py-2.5 sm:py-3 px-2 rounded-[14px] border transition-all cursor-pointer ${isSelected
                            ? "border-[#1776F0] bg-[#1776F0] text-white shadow-md shadow-blue-500/20"
                            : "border-slate-200 bg-slate-50 hover:bg-white hover:border-[#1776F0]/60 text-slate-800"
                          }`}
                      >
                        <span className={`text-[11px] font-medium capitalize ${isSelected ? "text-white/90" : "text-slate-500"}`}>
                          {slot.month}
                        </span>
                        <span className="text-lg sm:text-xl font-bold my-0.5 leading-tight">
                          {slot.day}
                        </span>
                        <span className={`text-[11px] font-semibold ${isSelected ? "text-white/80" : "text-[#1776F0]"}`}>
                          {weekday}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Time Slots Section */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-bold text-slate-900">
                      Select Available Time Slot
                    </p>
                  </div>

                  {/* Mobile Dropdown */}
                  <div className="sm:hidden mb-3">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none text-[#1776F0]">
                        <Clock3 size={15} />
                      </div>
                      <select
                        value={selectedSlotTime}
                        onChange={(e) => setSelectedSlotTime(e.target.value)}
                        disabled={isFetchingSlots || availableSlotList.length === 0}
                        className={`w-full appearance-none rounded-xl border bg-slate-50/80 pl-10 pr-10 py-3 text-xs font-semibold text-slate-800 transition shadow-sm focus:bg-white focus:outline-none focus:ring-2 ${
                          selectedSlotTime
                            ? "border-[#1776F0] bg-blue-50/30 ring-2 ring-[#1776F0]/20 text-[#1776F0]"
                            : "border-slate-300 focus:border-[#1776F0] focus:ring-[#1776F0]/20"
                        }`}
                      >
                        <option value="">
                          {isFetchingSlots
                            ? "Loading available slots..."
                            : availableSlotList.length === 0
                              ? "No slots available for this date"
                              : "-- Select a Time Slot --"}
                        </option>
                        {availableSlotList.map((slot) => (
                          <option
                            key={slot.startUtc}
                            value={slot.time}
                            disabled={slot.isUnavailable}
                          >
                            {slot.time} {slot.isUnavailable ? "(Booked / Unavailable)" : ""}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3.5 pointer-events-none text-slate-400">
                        <ChevronDown size={16} />
                      </div>
                    </div>
                  </div>

                  {/* Desktop Quick Grid */}
                  {isFetchingSlots ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2.5">
                      {[1, 2, 3, 4, 5, 6].map((i) => (
                        <Skeleton key={i} className="h-11 rounded-xl bg-slate-100" />
                      ))}
                    </div>
                  ) : availableSlotList.length === 0 ? (
                    <div className="p-6 text-center rounded-xl bg-slate-50 border border-slate-200">
                      <p className="text-sm text-slate-600">
                        No slots available for this date. Please select another date above.
                      </p>
                    </div>
                  ) : (
                    <div className="hidden sm:grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2.5 max-h-[220px] overflow-y-auto p-1">
                      {availableSlotList.map((slot) => {
                        const isSelected = selectedSlotTime === slot.time;
                        return (
                          <button
                            key={slot.startUtc}
                            type="button"
                            disabled={slot.isUnavailable}
                            onClick={() => setSelectedSlotTime(slot.time)}
                            className={`py-2.5 px-3 rounded-xl text-xs sm:text-sm font-semibold border transition cursor-pointer ${
                              slot.isUnavailable
                                ? "bg-rose-50/50 border-rose-200 text-rose-400 line-through cursor-not-allowed"
                                : isSelected
                                  ? "bg-[#1776F0] text-white border-[#1776F0] shadow-md ring-2 ring-[#1776F0]/20"
                                  : "bg-emerald-50/60 border-emerald-300 text-emerald-800 hover:border-emerald-500 hover:bg-emerald-100/80"
                            }`}
                          >
                            {slot.time}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Continue / Confirm Booking Button */}
                <div className="flex items-center justify-end pt-1">
                  <button
                    type="button"
                    disabled={!selectedSlotTime || isFetchingSlots || isSubmittingBooking}
                    onClick={handleProceedToDetails}
                    className="w-full sm:w-auto min-w-[200px] py-3 px-6 rounded-full gradient_button_shadow bg-button-gradient active:scale-95 text-white font-semibold text-xs sm:text-sm transition hover:opacity-95 shadow-md cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none flex items-center justify-center gap-2"
                  >
                    {isSubmittingBooking ? (
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : user && !user.isAnonymous ? (
                      selectedSlotTime ? `Confirm & Book ${selectedSlotTime} Slot →` : "Select a Slot to Confirm →"
                    ) : (
                      selectedSlotTime ? `Continue with ${selectedSlotTime} Slot →` : "Select a Slot to Continue →"
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* ────────── STEP 3: CONTACT FORM ────────── */}
          {expertStep === "contact-form" && (
            <motion.div
              key="contact-step"
              variants={stepVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="pt-4"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3.5">
                <button
                  type="button"
                  onClick={() => setExpertStep("slot-picker")}
                  className="text-xs sm:text-sm font-semibold text-slate-600 hover:text-black flex items-center gap-1 transition shrink-0"
                >
                  ← Back to Change Slot
                </button>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-[11px] sm:text-xs font-semibold text-slate-800 shrink-0">
                  🗓️ {selectedDate} at {selectedSlotTime}
                </span>
              </div>

              {isOtpSent ? (
                /* Clean OTP Verification Card */
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white border border-slate-200 rounded-[18px] sm:rounded-[22px] p-5 sm:p-7 shadow-sm"
                >
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
                    <div>
                      <p className="text-sm sm:text-base font-bold text-slate-900">
                        Enter 6-digit Verification Code
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Sent to <strong className="text-slate-800">{phone}</strong>{" "}
                        <button
                          type="button"
                          onClick={() => {
                            setIsOtpSent(false);
                            setOtp("");
                            setOtpError("");
                          }}
                          className="text-[#1776F0] underline hover:text-blue-700 ml-1 font-semibold cursor-pointer"
                        >
                          Edit
                        </button>
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={resendCountdown > 0 || isSendingOtp}
                      className={`text-xs font-semibold ${
                        resendCountdown > 0
                          ? "text-slate-400 cursor-not-allowed"
                          : "text-[#1776F0] hover:underline cursor-pointer"
                      }`}
                    >
                      {resendCountdown > 0 ? `Resend in ${resendCountdown}s` : "Resend OTP"}
                    </button>
                  </div>

                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                    {/* OTP Inputs */}
                    <div className="flex justify-center sm:justify-start overflow-x-auto py-1">
                      <InputOTP
                        required
                        pattern={REGEXP_ONLY_DIGITS}
                        maxLength={6}
                        value={otp}
                        onChange={(val) => {
                          setOtp(val);
                          if (otpError) setOtpError("");
                        }}
                      >
                        <InputOTPGroup className="gap-1.5 sm:gap-2">
                          <InputOTPSlot index={0} className="!w-9 !h-10 sm:!w-11 sm:!h-11 !rounded-lg sm:!rounded-xl !text-base sm:!text-lg !font-bold !border-slate-300 !bg-slate-50/70 focus:!bg-white focus:!border-[#1776F0] focus:!ring-2 focus:!ring-[#1776F0]/20 transition-all shadow-xs" />
                          <InputOTPSlot index={1} className="!w-9 !h-10 sm:!w-11 sm:!h-11 !rounded-lg sm:!rounded-xl !text-base sm:!text-lg !font-bold !border-slate-300 !bg-slate-50/70 focus:!bg-white focus:!border-[#1776F0] focus:!ring-2 focus:!ring-[#1776F0]/20 transition-all shadow-xs" />
                          <InputOTPSlot index={2} className="!w-9 !h-10 sm:!w-11 sm:!h-11 !rounded-lg sm:!rounded-xl !text-base sm:!text-lg !font-bold !border-slate-300 !bg-slate-50/70 focus:!bg-white focus:!border-[#1776F0] focus:!ring-2 focus:!ring-[#1776F0]/20 transition-all shadow-xs" />
                        </InputOTPGroup>
                        <InputOTPSeparator className="text-slate-400 font-bold mx-0.5 sm:mx-1" />
                        <InputOTPGroup className="gap-1.5 sm:gap-2">
                          <InputOTPSlot index={3} className="!w-9 !h-10 sm:!w-11 sm:!h-11 !rounded-lg sm:!rounded-xl !text-base sm:!text-lg !font-bold !border-slate-300 !bg-slate-50/70 focus:!bg-white focus:!border-[#1776F0] focus:!ring-2 focus:!ring-[#1776F0]/20 transition-all shadow-xs" />
                          <InputOTPSlot index={4} className="!w-9 !h-10 sm:!w-11 sm:!h-11 !rounded-lg sm:!rounded-xl !text-base sm:!text-lg !font-bold !border-slate-300 !bg-slate-50/70 focus:!bg-white focus:!border-[#1776F0] focus:!ring-2 focus:!ring-[#1776F0]/20 transition-all shadow-xs" />
                          <InputOTPSlot index={5} className="!w-9 !h-10 sm:!w-11 sm:!h-11 !rounded-lg sm:!rounded-xl !text-base sm:!text-lg !font-bold !border-slate-300 !bg-slate-50/70 focus:!bg-white focus:!border-[#1776F0] focus:!ring-2 focus:!ring-[#1776F0]/20 transition-all shadow-xs" />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>

                    {/* Verify Button */}
                    <button
                      type="button"
                      onClick={handleVerifyOtp}
                      disabled={isVerifyingOtp || isSubmittingBooking || otp.length < 6}
                      className="w-full sm:w-auto py-3 px-7 rounded-full gradient_button_shadow bg-button-gradient active:scale-95 text-white font-semibold text-sm transition hover:opacity-95 shadow-md cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                      {isVerifyingOtp || isSubmittingBooking ? (
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        "Verify & Book Session →"
                      )}
                    </button>
                  </div>

                  {otpError && (
                    <p className="text-xs text-rose-500 font-medium mt-2">{otpError}</p>
                  )}
                </motion.div>
              ) : (
                /* Contact Details Form */
                <form onSubmit={handleSendOtp} className="bg-white border border-slate-200 rounded-[18px] sm:rounded-[22px] p-3.5 sm:p-7 shadow-sm">
                  <p className="text-xs sm:text-sm text-slate-500 mb-4">
                    Enter your details to confirm your slot. We&apos;ll send a quick 6-digit OTP to verify your mobile number.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Full Name */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                        Full Name <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => {
                          setFullName(e.target.value);
                          if (e.target.value.trim()) setFullNameError("");
                        }}
                        placeholder="Your Full Name"
                        className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1776F0]/20 ${fullNameError ? "border-rose-400 bg-rose-50/30" : "border-slate-300 bg-slate-50/50"
                          }`}
                      />
                      {fullNameError && <p className="text-xs text-rose-500 mt-1">{fullNameError}</p>}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                        Email Address <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (e.target.value.trim()) setEmailError("");
                        }}
                        placeholder="Your Email"
                        className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1776F0]/20 ${emailError ? "border-rose-400 bg-rose-50/30" : "border-slate-300 bg-slate-50/50"
                          }`}
                      />
                      {emailError && <p className="text-xs text-rose-500 mt-1">{emailError}</p>}
                    </div>

                    {/* Mobile Number - Locked to India */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                        Mobile Number <span className="text-rose-500">*</span>
                      </label>
                      <div className={`rounded-xl border overflow-hidden ${phoneError ? "border-rose-400" : "border-slate-300"}`}>
                        <PhoneInput
                          defaultCountry="in"
                          forceDialCode
                          hideDropdown={true}
                          disableDialCodePrefill={false}
                          value={phone}
                          onChange={handlePhoneChange}
                          inputClassName="!w-full !text-sm !h-[46px] !bg-slate-50/50 !border-0 !text-slate-900"
                          countrySelectorStyleProps={{
                            buttonClassName: "!bg-slate-50/50 !border-0 !cursor-default !pointer-events-none",
                            dropdownStyleProps: {
                              style: { display: "none" },
                            },
                          }}
                          className="w-full bg-slate-50/50 flex items-center px-1"
                        />
                      </div>
                      {phoneError && <p className="text-xs text-rose-500 mt-1">{phoneError}</p>}
                    </div>
                  </div>

                  {/* Initial Actions: Back & Send OTP */}
                  <div className="flex items-center gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setExpertStep("slot-picker")}
                      className="px-6 py-3 rounded-full border border-slate-300 text-slate-700 font-semibold text-sm hover:bg-slate-50 transition cursor-pointer"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={isSendingOtp}
                      className="flex-1 sm:flex-none sm:min-w-[240px] py-3 px-6 rounded-full gradient_button_shadow bg-button-gradient active:scale-95 text-white font-semibold text-sm transition hover:opacity-95 shadow-md cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {isSendingOtp ? (
                        <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        "Send OTP to Confirm →"
                      )}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          )}

          {/* ────────── STEP 4: SUCCESS SCREEN MATCHING ATTACHED SCREENSHOT ────────── */}
          {expertStep === "success" && (
            <motion.div
              key="success-step"
              variants={stepVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="py-8 sm:py-12 flex flex-col items-center justify-center text-center max-w-[620px] mx-auto"
            >
              {/* Green Circle Checkmark Icon */}
              <div className="w-16 h-16 rounded-full bg-[#E5F3EB] flex items-center justify-center mb-5">
                <div className="w-10 h-10 rounded-full border-2 border-[#1776F0] flex items-center justify-center text-[#1776F0]">
                  <Check strokeWidth={3} size={20} />
                </div>
              </div>

              {/* Title e.g. "Booked, Nikhil Mehra!" with clean modern typography */}
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mb-1.5 ff_poppins">
                {fullName && !fullName.toLowerCase().includes("null")
                  ? `Booked, ${fullName.trim()}!`
                  : "Session Booked!"}
              </h2>

              {/* Subtitle e.g. "Your expert will reach out at Sep 17 at 10:45 AM." */}
              <p className="text-sm sm:text-base text-slate-600 mb-6 font-medium">
                Your expert will reach out at {formattedSlotReachout}.
              </p>

              {/* 2 White Card Blocks: Calendar Invite & WhatsApp Confirmation */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 w-full mb-8">
                {/* Calendar Invite Card */}
                <div className="bg-white border border-slate-200/90 rounded-[20px] p-5 flex flex-col items-center justify-center text-center shadow-sm">
                  <Mail className="w-5 h-5 text-[#1776F0] mb-2" />
                  <span className="text-[10px] sm:text-[11px] font-extrabold tracking-wider text-slate-500 uppercase mb-1">
                    CALENDAR INVITE
                  </span>
                  <p className="text-sm sm:text-base font-bold text-slate-900 truncate max-w-full px-2">
                    {email && !email.toLowerCase().includes("null") ? email : "Sent to your email"}
                  </p>
                </div>

                {/* WhatsApp Confirmation Card */}
                <div className="bg-white border border-slate-200/90 rounded-[20px] p-5 flex flex-col items-center justify-center text-center shadow-sm">
                  <MessageSquare className="w-5 h-5 text-[#8F3EBC] mb-2" />
                  <span className="text-[10px] sm:text-[11px] font-extrabold tracking-wider text-slate-500 uppercase mb-1">
                    WHATSAPP CONFIRMATION
                  </span>
                  <p className="text-sm sm:text-base font-bold text-slate-900 truncate max-w-full px-2">
                    {phone && !phone.toLowerCase().includes("null") ? phone : "Sent to your phone"}
                  </p>
                </div>
              </div>

              {/* Done / Go to Sessions Button */}
              <button
                type="button"
                onClick={() => {
                  handleClose();
                  router.push("/dashboard/sessions");
                }}
                className="py-3 px-12 sm:px-16 bg-[#0E131F] hover:bg-[#1c2438] active:scale-95 rounded-full text-white font-semibold text-sm sm:text-base shadow-sm hover:shadow transition cursor-pointer flex items-center gap-2"
              >
                Go to Sessions →
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
};

export default FloatingExpertCallModal;
